/**
 * Navigation Constants for Olvidadme
 * 
 * Centralized navigation routes following DRY principle from PATTERNS_FROM_BASKET_BASE.md
 * Single source of truth for all navigation routes across the application
 * 
 * This file contains:
 * - All application routes organized by category
 * - Helper functions for route management
 * - Route metadata and configurations
 * 
 * Following ADR-071: Centralized Navigation Constants Pattern
 */

// Public routes - Accessible without authentication
export const PUBLIC_ROUTES = {
  // Landing and main pages
  HOME: '/',
  ABOUT_US: '/quienes-somos',
  PRICING: '/pricing', // Página consolidada de servicios y precios (ADR-032)
  SERVICES: '/pricing', // Alias - /services redirige a /pricing (ADR-032)
  BLOG: '/blog',
  BLOG_ARTICLE: '/blog/:slug',
  BLOG_CATEGORY: '/blog/categoria/:category',

  // Service detail pages (se mantienen para detalles específicos)
  SERVICE_OLVIDO: '/services/derecho-olvido',
  SERVICE_POSTMORTEM: '/services/derecho-olvido-postmortem',
  SERVICE_DOCUMENTOS: '/services/documentos-legales'
}

// Authentication routes
export const AUTH_ROUTES = {
  LOGIN: '/auth/login',
  REGISTER: '/auth/register',
  FORGOT_PASSWORD: '/auth/forgot-password',
  RESET_PASSWORD: '/auth/reset-password',
  VERIFY_EMAIL: '/auth/verify-email'
}

// Payment routes
export const PAYMENT_ROUTES = {
  SUCCESS: '/payment/success',
  CANCELLED: '/payment/cancelled',
  FAILED: '/payment/failed'
}

// Legal and compliance routes
export const LEGAL_ROUTES = {
  PRIVACY_POLICY: '/politica-privacidad',
  TERMS_CONDITIONS: '/terminos-condiciones',
  LEGAL_NOTICE: '/aviso-legal',
  COOKIE_POLICY: '/politica-cookies',
  HELP_CENTER: '/centro-ayuda',
  RGPD_INFO: '/legal/rgpd-info', // ✅ IMPLEMENTADO - Información RGPD
  CONTACT: '/support/contact', // ✅ IMPLEMENTADO - Formulario de contacto
  LEGAL_HELP: '/help/legal' // To be created or removed
}

// Protected routes - Require authentication
export const PROTECTED_ROUTES = {
  // User dashboard and profile
  DASHBOARD: '/dashboard',
  PROFILE: '/profile',
  // SETTINGS consolidated into PROFILE - redirect for legacy compatibility
  SETTINGS: '/profile', // Legacy redirect - Settings now part of Profile

  // Case management
  CASES: '/cases',
  CASE_CREATE: '/cases/create',
  CASE_DETAIL: '/cases/:id',

  // Documents
  DOCUMENTS: '/documents',

  // Library - Static document templates (Sprint 4)
  LIBRARY: '/library'
}

// GDPR specific routes
export const GDPR_ROUTES = {
  CONSENT: '/gdpr/consent', // ✅ IMPLEMENTADO - Gestión de consentimientos GDPR
  EXPORT: '/gdpr/export', // ✅ IMPLEMENTADO - Exportar datos personales  
  DELETE: '/gdpr/delete', // ✅ IMPLEMENTADO - Eliminar cuenta y datos
  DATA: '/gdpr/data', // ✅ IMPLEMENTADO - Ver mis datos GDPR
  DATA_EXPORT: '/gdpr/data-export' // Alternative route - To be consolidated
}

// Admin routes
export const ADMIN_ROUTES = {
  DASHBOARD: '/admin',
  CASES: '/admin/cases',
  USERS: '/admin/users',
  LAWYERS: '/admin/lawyers', // ✅ IMPLEMENTADO - Gestión de abogados
  STATS: '/admin/stats', // ✅ IMPLEMENTADO - Panel de estadísticas
  PAYMENTS: '/admin/payments', // ✅ IMPLEMENTADO (Sprint 04) - Gestión de pagos
  AUDIT_LOGS: '/admin/audit-logs', // ✅ IMPLEMENTADO (TASK-007) - Registro de auditoría
  ARCHIVO_LEGAL: '/admin/archivo-legal', // ✅ IMPLEMENTADO (Sprint RGPD) - Archivo legal retención RGPD
  LIBRARY: '/admin/library', // ✅ IMPLEMENTADO (Sprint 3) - Biblioteca de documentos
  BLOG: '/admin/blog',
  BLOG_NEW: '/admin/blog/posts/new',
  BLOG_EDIT: '/admin/blog/posts/:id/edit'
}

// TODO POST-MVP: Lawyer specific routes - Backend preparado, frontend pospuesto
// Ver docs/POST_MVP_LAWYER_IMPLEMENTATION.md para roadmap completo de implementación
// Decisión ADR-072: Limpieza temporal para foco MVP, reactivación en fase POST-MVP
export const LAWYER_ROUTES = {
  CASES: '/lawyer/cases', // TODO POST-MVP: LawyerCases.vue - Dashboard casos asignados  
  CALENDAR: '/lawyer/calendar', // TODO POST-MVP: LawyerCalendar.vue - Agenda y citas
  TEMPLATES: '/lawyer/templates' // TODO POST-MVP: LawyerTemplates.vue - Plantillas legales
}

// Error pages
export const ERROR_ROUTES = {
  NOT_FOUND: '/404',
  FORBIDDEN: '/403',
  SERVER_ERROR: '/500'
}

// Combined navigation routes object for easy access
export const NAVIGATION_ROUTES = {
  ...PUBLIC_ROUTES,
  ...AUTH_ROUTES,
  ...PAYMENT_ROUTES,
  ...LEGAL_ROUTES,
  ...PROTECTED_ROUTES,
  ...GDPR_ROUTES,
  ...ADMIN_ROUTES,
  ...LAWYER_ROUTES,
  ...ERROR_ROUTES
}

// Route categories for grouping
export const ROUTE_CATEGORIES = {
  PUBLIC: 'public',
  AUTH: 'auth',
  PAYMENT: 'payment',
  LEGAL: 'legal',
  PROTECTED: 'protected',
  GDPR: 'gdpr',
  ADMIN: 'admin',
  LAWYER: 'lawyer',
  ERROR: 'error'
}

// Routes that don't require authentication
export const OPEN_ROUTES = [
  ...Object.values(PUBLIC_ROUTES),
  ...Object.values(AUTH_ROUTES),
  ...Object.values(PAYMENT_ROUTES),
  ...Object.values(LEGAL_ROUTES),
  ...Object.values(ERROR_ROUTES)
]

// Routes that require authentication
export const AUTHENTICATED_ROUTES = [
  ...Object.values(PROTECTED_ROUTES),
  ...Object.values(GDPR_ROUTES),
  ...Object.values(ADMIN_ROUTES),
  ...Object.values(LAWYER_ROUTES)
]

// Routes that require admin role
export const ADMIN_ONLY_ROUTES = Object.values(ADMIN_ROUTES)

// Routes that require lawyer role
export const LAWYER_ONLY_ROUTES = Object.values(LAWYER_ROUTES)

// Helper functions for route management

/**
 * Check if a route requires authentication
 * @param {string} route - The route path to check
 * @returns {boolean} True if route requires authentication
 */
export const isProtectedRoute = (route) => {
  // Handle dynamic routes by checking the base path
  const basePath = route.split('/').slice(0, -1).join('/')
  
  return AUTHENTICATED_ROUTES.some(authRoute => {
    // Handle dynamic segments like :id
    const authBasePath = authRoute.includes(':') 
      ? authRoute.split('/').slice(0, -1).join('/')
      : authRoute
    
    return authRoute === route || authBasePath === basePath || authRoute === basePath
  })
}

/**
 * Check if a route is public (no auth required)
 * @param {string} route - The route path to check
 * @returns {boolean} True if route is public
 */
export const isPublicRoute = (route) => {
  return !isProtectedRoute(route)
}

/**
 * Check if a route requires admin role
 * @param {string} route - The route path to check
 * @returns {boolean} True if route requires admin role
 */
export const isAdminRoute = (route) => {
  return route.startsWith('/admin')
}

/**
 * Check if a route requires lawyer role
 * @param {string} route - The route path to check
 * @returns {boolean} True if route requires lawyer role
 */
export const isLawyerRoute = (route) => {
  return route.startsWith('/lawyer')
}

/**
 * Get route category
 * @param {string} route - The route path
 * @returns {string} The category of the route
 */
export const getRouteCategory = (route) => {
  if (isAdminRoute(route)) return ROUTE_CATEGORIES.ADMIN
  if (isLawyerRoute(route)) return ROUTE_CATEGORIES.LAWYER
  if (route.startsWith('/auth')) return ROUTE_CATEGORIES.AUTH
  if (route.startsWith('/payment')) return ROUTE_CATEGORIES.PAYMENT
  if (route.startsWith('/gdpr')) return ROUTE_CATEGORIES.GDPR
  if (route.startsWith('/cases') || route.startsWith('/dashboard') || route.startsWith('/profile')) {
    return ROUTE_CATEGORIES.PROTECTED
  }
  if (route.includes('legal') || route.includes('politica') || route.includes('terminos')) {
    return ROUTE_CATEGORIES.LEGAL
  }
  if (route.includes('404') || route.includes('403') || route.includes('500')) {
    return ROUTE_CATEGORIES.ERROR
  }
  return ROUTE_CATEGORIES.PUBLIC
}

/**
 * Build dynamic route with parameters
 * @param {string} template - Route template with :param placeholders
 * @param {object} params - Parameters to replace in template
 * @returns {string} The built route
 */
export const buildRoute = (template, params = {}) => {
  let route = template
  Object.keys(params).forEach(key => {
    route = route.replace(`:${key}`, params[key])
  })
  return route
}

/**
 * Get route by name (searches all route collections)
 * @param {string} name - The route name/key to find
 * @returns {string|null} The route path or null if not found
 */
export const getRouteByName = (name) => {
  // Search in all route collections
  const collections = [
    PUBLIC_ROUTES,
    AUTH_ROUTES,
    PAYMENT_ROUTES,
    LEGAL_ROUTES,
    PROTECTED_ROUTES,
    GDPR_ROUTES,
    ADMIN_ROUTES,
    LAWYER_ROUTES,
    ERROR_ROUTES
  ]
  
  for (const collection of collections) {
    if (collection[name]) {
      return collection[name]
    }
  }
  
  return null
}

// Export default object for convenient destructuring
export default {
  PUBLIC_ROUTES,
  AUTH_ROUTES,
  PAYMENT_ROUTES,
  LEGAL_ROUTES,
  PROTECTED_ROUTES,
  GDPR_ROUTES,
  ADMIN_ROUTES,
  LAWYER_ROUTES,
  ERROR_ROUTES,
  NAVIGATION_ROUTES,
  ROUTE_CATEGORIES,
  OPEN_ROUTES,
  AUTHENTICATED_ROUTES,
  ADMIN_ONLY_ROUTES,
  LAWYER_ONLY_ROUTES,
  isProtectedRoute,
  isPublicRoute,
  isAdminRoute,
  isLawyerRoute,
  getRouteCategory,
  buildRoute,
  getRouteByName
}